// ─── AutoChinBot · task7 — Secondary Alert System ───────────────
// The "hệ thống cảnh báo phụ". Users install alerts on Telegram via `/alert`
// (Price / Reach MA / Break); this browser engine watches the LIVE market and
// notifies Telegram the moment a condition is met — ONE message per alert,
// never spamming.
//
// Three alert families (matching task7's 3 nhiệm vụ):
//   1) price     — notify when the price touches a value X.
//                  params = { price }
//   2) reach_ma  — notify when the price comes within x pips of an MA line
//                  (SMA14/SMA20/SMA50/HMA50/HMA100/TMA50).
//                  params = { ma, distance }
//   3) break     — notify on a candle CLOSE that breaks:
//                    • an MA (SMA14/SMA20/SMA50/HMA50/HMA100) — a cross-through
//                    • a price level Min/Max over the previous 50/100/200
//                      closed candles (task2 levels)
//                  params = { kind }  (kind = 'SMA50' | 'Min50' | 'Max200' …)
//
// Where the work happens:
//   • price / reach_ma  → tick-by-tick (ctraderPriceBus + the engine's own
//                           account-scoped live bid/ask).
//   • break             → on every CLOSED candle (edata.onCandleClose).
//
// Alerts are stored in `telegram_bot_alerts` (status active|fired|cancelled).
// This engine only READS active alerts; the actual claim (active → fired) and
// the Telegram send happen server-side in the `fire-alert` edge action, so an
// alert fires exactly once even across multiple accounts/runtimes.
//
// Runs in the background (module-level singleton via the task runtime) exactly
// like task1–task6 — the app must be open for detection to run.

import { supabase } from '@/lib/supabase';
import telegramBot from '@/lib/telegram-bot-client';
import { ctraderPriceBus } from '@/lib/ctrader-price-bus';
import type { AutoChinBotEngine } from '@/lib/auto-chin-bot-engine';
import type { AutoChinEdataEngine, AutoChinIndicatorSeries, EdataCandleCloseEvent } from '@/lib/autochin-edata-engine';
import type { AutoChinLogInput } from '@/lib/autochin-system-log';

// ─── Types ──────────────────────────────────────────────────────

export interface AutoChinAlert {
  id: string;
  chat_id: string | null;
  telegram_account_id: number | null;
  symbol: string | null;
  symbol_id: number | null;
  alert_type: string;
  params: Record<string, unknown> | null;
  status: string;
}

/** MA label (as shown in the keyboard) → edata indicator series key. */
const MA_SERIES: Record<string, keyof AutoChinIndicatorSeries> = {
  SMA14: 'sma14',
  SMA20: 'sma20',
  SMA50: 'sma50',
  HMA50: 'hma50',
  HMA100: 'hma100',
  TMA50: 'tma50',
};

const ACTIVE_STATUSES = ['active'];
const POLL_INTERVAL_MS = 8000;
const TICK_THROTTLE_MS = 200;

const LEVEL_RE = /^(Min|Max)(\d+)$/;

// ─── Controller ─────────────────────────────────────────────────

export class AutoChinAlertController {
  private bot: AutoChinBotEngine;
  private edata: AutoChinEdataEngine;
  private userId: string;
  private strategyId: string;
  private accountId: string;

  private logListeners = new Set<(l: AutoChinLogInput) => void>();
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private unsubPrice: (() => void) | null = null;
  private unsubCandle: (() => void) | null = null;

  private started = false;
  private polling = false;

  /** Active alerts keyed by id. */
  private alerts = new Map<string, AutoChinAlert>();
  /** Alerts already fired (or being fired) — never re-checked. */
  private settled = new Set<string>();
  private firing = new Set<string>();

  /** Last observed mid price per symbol — used for the price-touch crossing. */
  private lastMid: Record<number, number> = {};
  private lastTickCheck = 0;

  constructor(
    bot: AutoChinBotEngine,
    edata: AutoChinEdataEngine,
    userId: string,
    strategyId: string,
    accountId: string,
  ) {
    this.bot = bot;
    this.edata = edata;
    this.userId = userId;
    this.strategyId = strategyId;
    this.accountId = accountId;
  }

  // ─── Lifecycle ──────────────────────────────────────────────

  start(): void {
    if (this.started) return;
    this.started = true;
    void this.poll();
    this.pollTimer = setInterval(() => { void this.poll(); }, POLL_INTERVAL_MS);
    this.unsubPrice = ctraderPriceBus.subscribe((tick) => this.onTick(tick.symbolId));
    this.unsubCandle = this.edata.onCandleClose((e) => this.onCandleClose(e));
  }

  stop(): void {
    this.started = false;
    if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
    if (this.unsubPrice) { this.unsubPrice(); this.unsubPrice = null; }
    if (this.unsubCandle) { this.unsubCandle(); this.unsubCandle = null; }
  }

  onLog(fn: (l: AutoChinLogInput) => void): () => void {
    this.logListeners.add(fn);
    return () => { this.logListeners.delete(fn); };
  }

  // ─── Poll the active alerts ─────────────────────────────────

  private async poll(): Promise<void> {
    if (this.polling || !this.accountId) return;
    this.polling = true;
    try {
      const { data, error } = await supabase
        .from('telegram_bot_alerts')
        .select('id, chat_id, telegram_account_id, symbol, symbol_id, alert_type, params, status')
        .in('status', ACTIVE_STATUSES)
        .order('created_at', { ascending: false })
        .limit(200);
      if (error || !data) return;

      const next = new Map<string, AutoChinAlert>();
      for (const row of data as AutoChinAlert[]) {
        if (this.settled.has(row.id)) continue;
        next.set(row.id, row);
      }
      this.alerts = next;
    } catch {
      /* network hiccup — retry next tick */
    } finally {
      this.polling = false;
    }
  }

  // ─── Tick-by-tick: price touch + reach MA ───────────────────

  private onTick(symbolId: number): void {
    if (this.alerts.size === 0) return;
    const now = Date.now();
    if (now - this.lastTickCheck < TICK_THROTTLE_MS) return;
    this.lastTickCheck = now;

    const quotes = this.quotesFor(symbolId);
    if (!quotes) return;
    const mid = (quotes.bid + quotes.ask) / 2;
    const prev = this.lastMid[symbolId];
    this.lastMid[symbolId] = mid;

    for (const alert of this.alerts.values()) {
      if (this.settled.has(alert.id)) continue;
      if (this.alertSymbolId(alert) !== symbolId) continue;

      if (alert.alert_type === 'price') {
        this.checkPriceTouch(alert, prev, mid);
      } else if (alert.alert_type === 'reach_ma') {
        this.checkReachMa(alert, symbolId, mid);
      }
    }
  }

  /** price alert — fire when the price CROSSES the target between two ticks. */
  private checkPriceTouch(alert: AutoChinAlert, prev: number | undefined, mid: number): void {
    if (prev === undefined) return; // need a previous tick to detect a crossing
    const target = Number((alert.params || {}).price);
    if (!Number.isFinite(target) || target <= 0) return;
    const touched = (prev < target && mid >= target) || (prev > target && mid <= target);
    if (!touched) return;
    void this.fire(alert, { price: Number(mid.toFixed(5)) });
  }

  /** reach MA — fire when |price − MA| ≤ configured distance (pips). */
  private checkReachMa(alert: AutoChinAlert, symbolId: number, mid: number): void {
    const ed = this.edata.get(symbolId);
    if (!ed) return;
    const p = alert.params || {};
    const series = MA_SERIES[String(p.ma)];
    if (!series) return;
    const distance = Number(p.distance);
    if (!Number.isFinite(distance) || distance <= 0) return;

    const maVal = ed.indicators[series][0];
    if (maVal == null) return; // not enough candles yet

    const gapPips = Math.abs(mid - maVal) / ed.pipSize;
    if (gapPips > distance) return;
    void this.fire(alert, { distance: gapPips, ma: String(p.ma) });
  }

  // ─── Candle close: break detection ──────────────────────────

  private onCandleClose(e: EdataCandleCloseEvent): void {
    if (this.alerts.size === 0) return;
    for (const alert of this.alerts.values()) {
      if (this.settled.has(alert.id)) continue;
      if (alert.alert_type !== 'break') continue;
      if (this.alertSymbolId(alert) !== e.symbolId) continue;
      this.checkBreak(alert, e);
    }
  }

  private checkBreak(alert: AutoChinAlert, e: EdataCandleCloseEvent): void {
    const kind = String((alert.params || {}).kind || '');
    if (!kind) return;

    const lvl = kind.match(LEVEL_RE);
    if (lvl) {
      this.checkLevelBreak(alert, e, lvl[1] as 'Min' | 'Max', Number(lvl[2]));
      return;
    }
    const series = MA_SERIES[kind];
    if (series) this.checkMaBreak(alert, e, kind, series);
  }

  /**
   * Price-level break — the A0 close must be BELOW the Min (or ABOVE the Max)
   * of the previous N closed candles. We EXCLUDE A0 from the range: including
   * A0's own close in its own min/max makes a break impossible. Requires a full
   * window of prior candles so an early, half-loaded buffer never false-fires.
   */
  private checkLevelBreak(
    alert: AutoChinAlert, e: EdataCandleCloseEvent, side: 'Min' | 'Max', window: number,
  ): void {
    const candles = e.edata.candles;
    const a0 = candles[0];
    if (!a0) return;
    const prior = candles.slice(1, 1 + window);
    if (prior.length < window) return; // not enough history yet

    let level = side === 'Min' ? Infinity : -Infinity;
    for (const c of prior) {
      if (side === 'Min') { if (c.close < level) level = c.close; }
      else { if (c.close > level) level = c.close; }
    }
    if (!Number.isFinite(level)) return;

    const broke = side === 'Min' ? a0.close < level : a0.close > level;
    if (!broke) return;
    const kind = side + window;
    void this.fire(alert, { kind, level: Number(level.toFixed(5)), close: Number(a0.close.toFixed(5)) });
  }

  /** MA break — a candle CLOSE crossing the MA line A1 → A0 (like task4). */
  private checkMaBreak(
    alert: AutoChinAlert, e: EdataCandleCloseEvent, maLabel: string, series: keyof AutoChinIndicatorSeries,
  ): void {
    const a0 = e.edata.candles[0];
    const a1 = e.edata.candles[1];
    if (!a0 || !a1) return;
    const ma0 = e.edata.indicators[series][0];
    const ma1 = e.edata.indicators[series][1];
    if (ma0 == null || ma1 == null) return;

    const crossedUp = a1.close <= ma1 && a0.close >= ma0;
    const crossedDown = a1.close >= ma1 && a0.close <= ma0;
    if (!crossedUp && !crossedDown) return;

    const direction: 'UP' | 'DOWN' = crossedUp && !crossedDown
      ? 'UP'
      : crossedDown && !crossedUp
        ? 'DOWN'
        : a0.close >= ma0 ? 'UP' : 'DOWN';
    void this.fire(alert, { kind: maLabel, direction });
  }

  // ─── Fire (edge claims it atomically) ───────────────────────

  private async fire(alert: AutoChinAlert, context: Record<string, unknown>): Promise<void> {
    if (this.settled.has(alert.id) || this.firing.has(alert.id)) return;
    this.firing.add(alert.id);
    try {
      const res = await telegramBot.fireAlert(alert.id, context);
      if (res.success) {
        // success (even `already`) → treat as done; the edge claimed it.
        this.settled.add(alert.id);
        this.alerts.delete(alert.id);
        this.emitLog({
          level: 'success',
          message: `Cảnh báo · ${alert.symbol || '?'} — đã gửi thông báo (${alert.alert_type})`,
        });
      } else {
        this.firing.delete(alert.id); // retry on a later tick
      }
    } catch {
      this.firing.delete(alert.id);
    }
  }

  // ─── Helpers ────────────────────────────────────────────────

  /** The symbolId this alert targets, resolved against the engine's symbols. */
  private alertSymbolId(alert: AutoChinAlert): number | null {
    const wanted = String(alert.symbol || '').trim().toUpperCase();
    const snap = this.bot.snapshot();
    if (wanted) {
      for (const idStr of Object.keys(snap.symbols)) {
        const id = Number(idStr);
        const buf = snap.symbols[id];
        if (buf && buf.symbolName.toUpperCase() === wanted) return id;
      }
    }
    if (alert.symbol_id && snap.symbols[alert.symbol_id]) return alert.symbol_id;
    return alert.symbol_id ?? null;
  }

  /** Account-scoped live quotes read from this engine's own buffers. */
  private quotesFor(symbolId: number): { bid: number; ask: number } | null {
    const buf = this.bot.snapshot().symbols[symbolId];
    if (!buf || buf.currentBid == null || buf.currentAsk == null) return null;
    if (buf.currentBid <= 0 || buf.currentAsk <= 0) return null;
    return { bid: buf.currentBid, ask: buf.currentAsk };
  }

  private emitLog(input: AutoChinLogInput): void {
    this.logListeners.forEach((fn) => {
      try { fn(input); } catch { /* ignore */ }
    });
  }
}
